<template>
  <div class="menu-page">
    <div class="page-container">
      <!-- <h1 class="page-title">{{ menuInfo.name || '页面' }}</h1> -->
      <div v-if="loading" class="loading">
        <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
        <span>加载中...</span>
      </div>
      <!-- 详情页 -->
      <DynamicDetail v-else-if="isDetailPage" :detail-id="detailId" />
      <!-- 根据 code 动态加载不同组件 -->
      <component v-else-if="currentComponent" :is="currentComponent" :data="componentData" />
      <!-- 默认 HTML 内容显示 -->
      <div v-else-if="menuContent" class="page-content" v-html="menuContent"></div>
      <div v-else class="empty-content">
        <p>暂无内容</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getMenuHtmlDetail } from '@/api/menu'
import CompanyProfile from './components/CompanyProfile.vue'
import DevelopmentHistory from './components/DevelopmentHistory.vue'
import IntegratedRetail from './components/IntegratedRetail.vue'
import CoreAdvantage from './components/CoreAdvantage.vue'
import Cooperation from './components/Cooperation.vue'
import ContactUs from './components/ContactUs.vue'
import GeneralMenu from './components/GeneralMenu.vue'
import DynamicDetail from './components/DynamicDetail.vue'

// 组件映射表：code -> 组件
const componentMap = {
  // 公司简介
  'company': CompanyProfile,
  'companyprofile': CompanyProfile,
  'about': CompanyProfile,
  // 发展历程
  'history': DevelopmentHistory,
  'development': DevelopmentHistory,
  'developmenthistory': DevelopmentHistory,
  // 一体化新零售
  'retail': IntegratedRetail,
  'integrated': IntegratedRetail,
  'integratedretail': IntegratedRetail,
  // 核心优势
  'advantage': CoreAdvantage,
  'core': CoreAdvantage,
  'coreadvantage': CoreAdvantage,
  // 关于合作
  'cooperation': Cooperation,
  'cooperate': Cooperation,
  // 联系我们
  'contact': ContactUs,
  'contactus': ContactUs
}

export default {
  name: 'MenuPage',
  components: {
    CompanyProfile,
    DevelopmentHistory,
    IntegratedRetail,
    CoreAdvantage,
    Cooperation,
    ContactUs,
    GeneralMenu,
    DynamicDetail
  },
  data () {
    return {
      menuInfo: {},
      menuContent: '',
      loading: false,
      componentData: {},
      currentComponent: null
    }
  },
  computed: {
    isDetailPage () {
      return !!this.$route.params.id
    },
    detailId () {
      return this.$route.params.id || ''
    },
    // 从 Vuex 获取所有菜单
    allMenus () {
      return this.$store.getters.allMenus
    }
  },
  watch: {
    '$route' (to, from) {
      // 路由变化时重新加载内容
      if (to.params.code !== from.params.code || to.params.id !== from.params.id) {
        if (to.params.id) {
          // 详情页，不需要加载菜单内容
          this.loading = false
        } else {
          this.loadMenuContent()
        }
      }
    }
  },
  mounted () {
    if (!this.isDetailPage) {
      this.loadMenuContent()
    }
  },
  methods: {
    async loadMenuContent () {
      const code = this.$route.params.code
      if (!code) {
        return
      }

      this.loading = true
      try {
        // 从 Vuex 中查找菜单信息（先通过 code，再通过 id）
        let menuItem = this.$store.getters.getMenuByCode(code)
        
        // 如果通过 code 没找到，尝试通过 ID 查找（如果 code 实际上是 ID）
        if (!menuItem) {
          menuItem = this.$store.getters.getMenuById(code)
        }

        if (menuItem && menuItem.id) {
          this.menuInfo = menuItem
          
          // 从 Vuex 获取父菜单信息（用于判断是否是产品中心的子菜单）
          let parentMenu = null
          if (menuItem.parentId && menuItem.parentId !== '-1') {
            parentMenu = this.$store.getters.getMenuById(menuItem.parentId)
          }
          
          // 判断是否是产品中心菜单或其子菜单
          const menuName = (menuItem.name || '').toLowerCase()
          const parentName = (parentMenu ? parentMenu.name : '').toLowerCase()
          const isProductCenterMenu = menuName.includes('产品中心') || parentName.includes('产品中心')
          
          // 如果是产品中心菜单，直接使用 GeneralMenu
          if (isProductCenterMenu) {
            this.currentComponent = GeneralMenu
            this.menuContent = ''
            try {
              const contentRes = await getMenuHtmlDetail({ webMenuId: menuItem.id })
              if (contentRes && contentRes.result) {
                const result = contentRes.result
                let parsedData = {}
                if (result.type === 1 && result.htmlContent) {
                  try {
                    parsedData = JSON.parse(result.htmlContent)
                  } catch (e) {
                    // 如果不是 JSON，使用空对象
                  }
                }
                // 传递菜单信息给 GeneralMenu
                this.componentData = {
                  ...parsedData,
                  menuCode: menuItem.code,
                  menuName: menuItem.name,
                  parentName: parentMenu ? parentMenu.name : '',
                  parentMenuId: parentMenu ? parentMenu.id : ''
                }
              } else {
                // 如果没有内容，也传递基本信息
                this.componentData = {
                  menuCode: menuItem.code,
                  menuName: menuItem.name,
                  parentName: parentMenu ? parentMenu.name : '',
                  parentMenuId: parentMenu ? parentMenu.id : ''
                }
              }
            } catch (contentError) {
              console.warn('获取菜单内容失败:', contentError)
              // 即使获取失败，也传递基本信息
              this.componentData = {
                menuCode: menuItem.code,
                menuName: menuItem.name,
                parentName: parentMenu ? parentMenu.name : '',
                parentMenuId: parentMenu ? parentMenu.id : ''
              }
            }
          } else {
            // 检查是否有对应的组件
            const menuCode = (menuItem.code || '').toLowerCase()
            const componentKey = this.getComponentKey(menuCode, menuItem.name)

            if (componentKey && componentMap[componentKey]) {
              // 使用组件显示
              this.currentComponent = componentMap[componentKey]
              this.menuContent = ''
              // 获取菜单内容并解析 JSON
              try {
                const contentRes = await getMenuHtmlDetail({ webMenuId: menuItem.id })
                if (contentRes && contentRes.result) {
                  const result = contentRes.result
                  if (result.type === 1 && result.htmlContent) {
                    try {
                      // 尝试解析 JSON
                      this.componentData = JSON.parse(result.htmlContent)
                    } catch (e) {
                      // 如果不是 JSON，尝试作为普通 HTML
                      console.warn('内容不是有效的 JSON，尝试作为 HTML 显示')
                      this.menuContent = result.htmlContent
                      this.currentComponent = null
                    }
                  } else if (result.type === 2 && result.htmlContent) {
                    this.menuContent = `<p><a href="${result.htmlContent}" target="_blank" rel="noopener noreferrer">${result.htmlContent}</a></p>`
                    this.currentComponent = null
                  }
                }
              } catch (contentError) {
                console.warn('获取菜单内容失败:', contentError)
              }
            } else {
              // 没有对应组件，尝试使用通用组件
              this.currentComponent = null
              try {
                const contentRes = await getMenuHtmlDetail({ webMenuId: menuItem.id })
                if (contentRes && contentRes.result) {
                  const result = contentRes.result
                  // type: 1-源码；2-链接
                  if (result.type === 1 && result.htmlContent) {
                    try {
                      // 尝试解析 JSON，如果有 bannerImage 等结构化数据，使用通用组件
                      const parsedData = JSON.parse(result.htmlContent)
                      if (parsedData.bannerImage || Object.keys(parsedData).length > 0) {
                        this.currentComponent = GeneralMenu
                        this.menuContent = ''
                        // 传递菜单信息以便判断是否需要显示列表
                        this.componentData = {
                          ...parsedData,
                          menuCode: menuItem.code,
                          menuName: menuItem.name,
                          parentName: parentMenu ? parentMenu.name : '',
                          parentMenuId: parentMenu ? parentMenu.id : ''
                        }
                      } else {
                        // 如果不是结构化数据，直接显示 HTML
                        this.menuContent = result.htmlContent
                      }
                    } catch (e) {
                      // 如果不是 JSON，直接显示 HTML
                      this.menuContent = result.htmlContent
                    }
                  } else if (result.type === 2 && result.htmlContent) {
                    // 链接类型，可以跳转或显示链接信息
                    this.menuContent = `<p><a href="${result.htmlContent}" target="_blank" rel="noopener noreferrer">${result.htmlContent}</a></p>`
                  }
                }
              } catch (contentError) {
                console.warn('获取菜单内容失败，可能该菜单没有内容:', contentError)
                this.menuContent = ''
              }
            }
          }
        } else {
          // 如果没有找到菜单，显示默认信息
          this.menuInfo = { name: code, code: code }
          this.menuContent = ''
        }
      } catch (error) {
        console.error('加载菜单内容失败:', error)
        this.menuInfo = { name: code, code: code }
        this.menuContent = ''
      } finally {
        this.loading = false
      }
    },
    // 根据 code 或 name 获取组件 key
    getComponentKey (code, name) {
      // 优先使用 code
      if (code) {
        const codeLower = code.toLowerCase()
        // 检查是否包含关键词
        if (codeLower.includes('company') || codeLower.includes('about') || codeLower.includes('profile')) {
          return 'company'
        }
        if (codeLower.includes('history') || codeLower.includes('development')) {
          return 'history'
        }
        if (codeLower.includes('retail') || codeLower.includes('integrated')) {
          return 'retail'
        }
        if (codeLower.includes('advantage') || codeLower.includes('core')) {
          return 'advantage'
        }
        if (codeLower.includes('cooperation') || codeLower.includes('cooperate')) {
          return 'cooperation'
        }
        if (codeLower.includes('contact')) {
          return 'contact'
        }
      }
      // 使用 name 匹配
      if (name) {
        const nameLower = name.toLowerCase()
        if (nameLower.includes('公司简介') || nameLower.includes('简介')) {
          return 'company'
        }
        if (nameLower.includes('发展历程') || nameLower.includes('历程') || nameLower.includes('历史')) {
          return 'history'
        }
        if (nameLower.includes('一体化') || nameLower.includes('新零售') || nameLower.includes('零售')) {
          return 'retail'
        }
        if (nameLower.includes('核心优势') || nameLower.includes('优势')) {
          return 'advantage'
        }
        if (nameLower.includes('关于合作') || nameLower.includes('合作')) {
          return 'cooperation'
        }
        if (nameLower.includes('联系我们') || nameLower.includes('联系')) {
          return 'contact'
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.menu-page {
  min-height: 60vh;
}

.page-container {
  margin: 0 auto;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #52c41a;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #666;
  gap: 12px;
}

.page-content {
  line-height: 1.8;
  color: #333;
}

.page-content ::v-deep img {
  max-width: 100%;
  height: auto;
}

.page-content ::v-deep p {
  margin-bottom: 16px;
}

.page-content ::v-deep h1,
.page-content ::v-deep h2,
.page-content ::v-deep h3 {
  margin-top: 24px;
  margin-bottom: 16px;
  color: #333;
}

.empty-content {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

/* 移动端 */
@media (max-width: 768px) {
  .menu-page {
    padding: 24px 16px;
  }
  .page-title {
    font-size: 24px;
  }
}
</style>
